import type { FC } from 'react';

import { useTranslation } from '@crehana/i18n';
import CloseSmall from '@crehana/icons/outline/CloseSmall';
import ExternalLink from '@crehana/icons/outline/ExternalLink';
import HappyChatBubble from '@crehana/icons/pictograms/HappyChatBubble';
import Idea from '@crehana/icons/pictograms/Idea';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors, Drawer } from '@crehana/ui';

import { ARTICLES, CONTACT_URL, FEEDBACK_URL, HELP_URL } from './constants';
import {
  Article,
  AssistanceWrapper,
  BeautyButton,
  ContentWrapper,
  Divider,
  FooterWrapper,
  HeaderWrapper,
  MailWrapper,
} from './HelpDrawer.styles';
import locales, { HELP_DRAWER, type THelpDrawerKeys } from './i18n';
import type { ExtendedDrawerProps } from './types';

const { baseLighter, white, baseMain, primaryMain } = colors;

const HelpDrawer: FC<React.PropsWithChildren<ExtendedDrawerProps>> = ({
  isDark = false,
  open,
  onDismiss,
  articles,
}) => {
  const { addResourceBundle, t } = useTranslation<THelpDrawerKeys>(HELP_DRAWER);
  const drawerArticles = articles || ARTICLES;
  const textColor = isDark ? 'text-white' : 'text-base-dark';

  addResourceBundle(locales);

  return (
    <Drawer
      open={open}
      className="flex flex-col h-full"
      onDismiss={onDismiss}
      width={{ xs: '100vw', sm: '70vw', md: '35vw', lg: '35vw' }}
      isDark={isDark}
    >
      <HeaderWrapper isDark={isDark}>
        <p className={`tw-font-subtitle3 ${textColor}`}>
          {t('HELP_DRAWER_TITLE')}
        </p>
        {/* TODO: talk with the code owner to fix this code so we can enable this rule again */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <div className="cursor-pointer" onClick={onDismiss}>
          <CloseSmall color={isDark ? white : baseLighter} />
        </div>
      </HeaderWrapper>

      <ContentWrapper>
        <p className="tw-font-label1 text-gray-dark mb-12">
          {t('HELP_DRAWER_RECOMMENDED')}
        </p>
        {drawerArticles.map((article, i) => (
          <Article
            // biome-ignore lint/suspicious/noArrayIndexKey: index as key is acceptable here
            key={`article-${i}`}
            className="tw-font-link1 mb-8"
            isDark={isDark}
            href={article.action}
            target="_blank"
          >
            {t(article.label as THelpDrawerKeys)}
          </Article>
        ))}
        <BeautyButton
          isDark={isDark}
          className="tw-font-caption mt-20"
          href={HELP_URL}
          target="_blank"
        >
          {t('HELP_DRAWER_HELP_CENTER')}
          <ExternalLink color="currentColor" className="ml-4" size={16} />
        </BeautyButton>
      </ContentWrapper>
      <FooterWrapper isDark={isDark}>
        <p className="tw-font-label1 text-gray-dark  py-12 px-20">
          {t('HELP_DRAWER_TECH_ASSISTANCE')}
        </p>
        <Divider isDark={isDark} />
        <AssistanceWrapper target="_blank" href={CONTACT_URL} isDark={isDark}>
          <HappyChatBubble
            size={28}
            className="mr-20"
            color={isDark ? white : baseMain}
            secondaryColor={isDark ? white : primaryMain}
          />
          <div>
            <p className={`tw-font-link1 ${textColor}`}>
              {t('HELP_DRAWER_DOUBTS_TITLE')}
            </p>
            <p className="tw-font-body5 text-gray-dark">
              {t('HELP_DRAWER_DOUBTS_SUBTITLE')}
            </p>
          </div>
        </AssistanceWrapper>
        <AssistanceWrapper target="_blank" href={FEEDBACK_URL} isDark={isDark}>
          <Idea size={28} className="mr-20" color={isDark ? white : baseMain} />
          <div>
            <p className={`tw-font-link1 ${textColor}`}>
              {t('HELP_DRAWER_FEEDBACK_TITLE')}
            </p>
            <p className="tw-font-body5 text-gray-dark">
              {t('HELP_DRAWER_FEEDBACK_SUBTITLE')}
            </p>
          </div>
        </AssistanceWrapper>
        <MailWrapper>
          <p className={`tw-font-body5 ${textColor}`}>
            {t('HELP_DRAWER_EMAIL_TITLE')}
          </p>
          <BeautyButton
            isDark={isDark}
            className="tw-font-caption"
            href="mailto:soporte.empresas@crehana.com"
          >
            soporte.empresas@crehana.com
          </BeautyButton>
        </MailWrapper>
      </FooterWrapper>
    </Drawer>
  );
};

export default HelpDrawer;
